import React from 'react';
import PropTypes from 'prop-types';
import { LuCloudUpload, LuDownload, LuEye, LuFileText, LuFolderOpen, LuIdCard, LuLock, LuTrash2, LuUpload } from 'react-icons/lu';
import Badge from '../../../../../components/ui/Badge';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { abs } from '../../engine/stage';
import { Abs, Btn, CARD, Note, SectionHead, TILE } from '../kit';

export const CV = (top) => ({
  list: { x: 256, y: top + 64, w: 480, h: 220 },
  row: { x: 272, y: top + 120, w: 448, h: 64 },
  view: { x: 532, y: top + 196, w: 56, h: 30 },
  download: { x: 594, y: top + 196, w: 56, h: 30 },
  delete: { x: 656, y: top + 196, w: 56, h: 30 },
  actions: { x: 528, y: top + 192, w: 190, h: 38 },
  upload: { x: 748, y: top + 64, w: 508, h: 444 },
  rules: { x: 772, y: top + 156, w: 460, h: 26 },
  template: { x: 772, y: top + 194, w: 460, h: 52 },
  drop: { x: 772, y: top + 258, w: 460, h: 116 },
  submit: { x: 1040, y: top + 452, w: 192, h: 40 },
});

const TYPE_OF_TAB = { 'cv-tr': 'CV-TR', 'cv-en': 'CV-EN', gcp: 'GCP', iata: 'IATA' };

/**
 * CV / GCP / IATA: the file list on the left and the upload card on the
 * right. state: { tab, chosen, uploading, uploaded, locked, hoverAction }
 */
export const CvPane = ({ top, state }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.cv.${key}`, vars);
  const C = CV(top);
  const tab = state.tab || 'cv-tr';
  const type = TYPE_OF_TAB[tab];
  const typeLabel = (key) => label(`types.${type}.${key}`);
  const uploaded = state.uploaded;
  return (
    <>
      <SectionHead top={top} icon={LuIdCard} gradient="tw-from-blue-400 tw-to-indigo-600 tw-shadow-blue-500/30" title={t(`profilePage.sections.${tab}.title`)} description={t(`profilePage.sections.${tab}.description`)} />
      <Abs rect={C.list} className={`tw-p-4 ${CARD} ${state.flash ? 'tw-ring-2 tw-ring-emerald-400/70' : ''}`}>
        <p className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{typeLabel('list')}{uploaded && <Badge tone="success">{label('uploaded')}</Badge>}</p>
        {!uploaded && (
          <div className="tw-mt-8 tw-flex tw-flex-col tw-items-center tw-text-center">
            <span className="tw-grid tw-h-12 tw-w-12 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05]"><LuFolderOpen aria-hidden="true" className="tw-h-6 tw-w-6" /></span>
            <p className="tw-mt-2 tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{typeLabel('empty')}</p>
            <p className="tw-text-xs tw-text-slate-500">{label('emptyText')}</p>
          </div>
        )}
      </Abs>
      {uploaded && (
        <>
          <Abs rect={C.row} className={`tw-flex tw-items-center tw-gap-3 tw-px-3 ${TILE}`}>
            <span className="tw-grid tw-h-10 tw-w-10 tw-place-items-center tw-rounded-xl tw-bg-rose-500/10 tw-text-rose-600"><LuFileText aria-hidden="true" className="tw-h-5 tw-w-5" /></span>
            <span className="tw-min-w-0">
              <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">Ayse_Kaya_CV.pdf</span>
              <span className="tw-block tw-text-[11px] tw-text-slate-500">11.10.2026 10:48 · 0,8 MB</span>
            </span>
          </Abs>
          {[['view', LuEye], ['download', LuDownload], ['delete', LuTrash2]].map(([key, Icon]) => (
            <span key={key} title={label(`actions.${key}`)} style={abs(C[key])} className={`tw-absolute tw-grid tw-place-items-center tw-rounded-full ${key === 'delete' ? 'tw-bg-rose-500/10 tw-text-rose-600' : 'tw-bg-slate-900/[0.05] tw-text-slate-600 dark:tw-bg-white/10 dark:tw-text-slate-300'} ${state.hoverAction === key ? 'tw-ring-2 tw-ring-teal-400/70' : ''}`}>
              <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
            </span>
          ))}
        </>
      )}
      <Abs rect={C.upload} className={`tw-p-5 ${CARD}`}>
        <p className="tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{typeLabel('upload')}</p>
        <p className="tw-mt-1 tw-line-clamp-2 tw-text-xs tw-leading-snug tw-text-slate-500">{typeLabel('description')}</p>
      </Abs>
      <Abs rect={C.rules} className="tw-flex tw-gap-2">
        {[label('rules.maxSize'), label('rules.formats'), tab === 'iata' ? label('rules.multiple') : label('rules.single')].map((rule) => (
          <span key={rule} className="tw-rounded-full tw-bg-slate-900/[0.05] tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-slate-600 dark:tw-bg-white/10 dark:tw-text-slate-300">{rule}</span>
        ))}
      </Abs>
      {(tab === 'cv-tr' || tab === 'cv-en') && (
        <Abs rect={C.template} className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-sky-400/40 tw-bg-sky-500/[0.07] tw-px-3 tw-transition-shadow tw-duration-300 ${state.templateFocus ? 'tw-ring-2 tw-ring-teal-400/60' : ''}`}>
          <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-sky-600" />
          <span className="tw-min-w-0">
            <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-sky-800 dark:tw-text-sky-200">{tab === 'cv-tr' ? label('templateTr') : label('templateEn')}</span>
            <span className="tw-block tw-text-[11px] tw-text-sky-700/80 dark:tw-text-sky-300/80">{label('templateHint')}</span>
          </span>
        </Abs>
      )}
      {uploaded && tab !== 'iata' ? (
        <Note rect={C.drop} tone="warning" icon={LuLock}>{label('locked', { type })}</Note>
      ) : (
        <Abs rect={C.drop} className={`tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-2xl tw-border-2 tw-border-dashed tw-transition-colors tw-duration-300 ${state.dropFocus ? 'tw-border-teal-500 tw-bg-teal-500/[0.06]' : 'tw-border-slate-300 tw-bg-slate-50/60 dark:tw-border-white/15 dark:tw-bg-white/[0.02]'}`}>
          <LuCloudUpload aria-hidden="true" className="tw-h-6 tw-w-6 tw-text-teal-600" />
          <span className="tw-mt-1 tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{state.chosen ? 'Ayse_Kaya_CV.pdf · 0,8 MB' : label('drop')}</span>
          <span className="tw-text-[11px] tw-text-slate-500">{label('dropHint')}</span>
        </Abs>
      )}
      <Btn rect={C.submit} variant="primary" icon={LuUpload} pressed={state.pressed} busy={state.uploading}>{state.uploading ? label('uploading') : typeLabel('upload')}</Btn>
    </>
  );
};

CvPane.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired };
